import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, TableWrap, Th, Td, Loading, Badge } from "../components/shared";
import { ROLES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Pencil } from "lucide-react";

const ROLE_CLS = { super_admin: "bg-violet-50 text-violet-700 border-violet-200", admin: "bg-blue-50 text-blue-700 border-blue-200", accountant: "bg-teal-50 text-teal-700 border-teal-200", data_entry: "bg-amber-50 text-amber-700 border-amber-200", viewer: "bg-slate-100 text-slate-600 border-slate-200" };

export default function Users() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const load = () => { setLoading(true); api.get("/users").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (edit?.id) await api.put(`/users/${edit.id}`, form);
      else await api.post("/users", form);
      toast.success("Saved"); setEdit(null); setForm({}); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <PageHeader title="User Management" subtitle="Role-based access control"
        actions={<Button onClick={() => { setEdit({}); setForm({ role: "data_entry" }); }} data-testid="add-user-btn" className="bg-[#0F2942] gap-1.5"><UserPlus className="h-4 w-4" /> Add User</Button>} />
      {loading ? <Loading /> : (
        <TableWrap><thead><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody>{items.map((u) => (
            <tr key={u.id}><Td className="font-medium">{u.name}</Td><Td>{u.email}</Td>
              <Td><Badge cls={ROLE_CLS[u.role]}>{ROLES.find((r) => r.value === u.role)?.label || u.role}</Badge></Td>
              <Td><Badge cls={u.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}>{u.status}</Badge></Td>
              <Td><button onClick={() => { setEdit(u); setForm({ name: u.name, role: u.role, status: u.status }); }} className="text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button></Td></tr>
          ))}</tbody></TableWrap>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent><DialogHeader><DialogTitle>{edit?.id ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="user-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            {!edit?.id && <div><Label className="text-xs">Email</Label><Input data-testid="user-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>}
            <div><Label className="text-xs">{edit?.id ? "New Password (optional)" : "Password"}</Label><Input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label className="text-xs">Role</Label>
              <select data-testid="user-role" value={form.role || "data_entry"} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-sm">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></div>
            {edit?.id && <div><Label className="text-xs">Status</Label>
              <select value={form.status || "active"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 rounded-lg border px-2 text-sm"><option value="active">Active</option><option value="inactive">Inactive</option></select></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} data-testid="user-save">Save</Button></DialogFooter>
        </DialogContent></Dialog>
    </div>
  );
}
